import type { FilterTree } from '@/app/components/query-builder/grouped-query';
import { getQueryMatch, type QueryMatch } from '@/app/components/query-builder/query-utils';
import type { QueryDefinition, QueryField, QueryRule } from '@/app/components/query-builder/types';
import { queryOperatorToWire } from '@/app/hooks/use-query-builder-filter';

export type WebsiteSignalFilterPayload = {
  filter?: FilterTree;
  match: QueryMatch;
  filters: Array<{ field: string; operator: string; value: unknown }>;
  targetPages?: Array<{
    conditions: Array<{ field: string; operator: string; value: unknown }>;
  }>;
};

export type WebsiteSignalFilterSummary = {
  id: string;
  field: string;
  fieldLabel: string;
  operatorLabel: string;
  valueLabel: string;
  valueLoading?: boolean;
  fieldDef?: QueryField;
  rule?: QueryRule;
};

const isTargetPageRule = (rule: QueryRule) =>
  ['url', 'visit_count', 'time_spent_seconds'].includes(rule.field);

export const buildWebsiteSignalFilterPayload = (
  query: QueryDefinition
): WebsiteSignalFilterPayload => {
  const rules = query.groups.flatMap(group => group.rules);
  const mapRule = (rule: QueryRule) => {
    const value =
      rule.field === 'time_spent_seconds' && rule.value !== '' ? Number(rule.value) : rule.value;

    return {
      field: rule.field,
      operator: queryOperatorToWire[rule.operator],
      value,
    };
  };
  const targetRules = rules.filter(isTargetPageRule);
  const targetPages = targetRules.map(rule => ({
    conditions: [
      mapRule(rule),
      ...(rule.children || [])
        .filter(child => child.value !== '' && child.value != null)
        .map(mapRule),
    ],
  }));

  return {
    match: getQueryMatch(query),
    filters: rules.filter(rule => !isTargetPageRule(rule)).map(mapRule),
    ...(targetPages.length ? { targetPages } : {}),
  };
};
